import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import type {
  ModuleAvailabilityPayload,
  VesselAndConnectedAccounts,
  VesselConnectedAccountAccess,
} from '@ankor-io/common/types/pay'

import { isPrivilegedRole } from '@ankor-io/common/domain/iam/account'

import { vesselService } from '@/api/services/vessel.service'
import { useUserContext } from '@/providers/user/useUserContext'

import { validateOrgId, validateVesselQueryParams } from './helpers'

export type CreateVesselMutationPayload = {
  name: string
  type?: AwxConnectedAccountType
  entityId?: string
}

type ListVesselsParams = {
  take?: number
  skip?: number
  enabled?: boolean
}

/**
 * TanStack Query hook for listing organization vessels.
 * Uses activeOrganizationId from context.
 */
export function useListOrganizationVesselsQuery(params?: ListVesselsParams) {
  const { activeOrganizationId } = useUserContext()
  const { enabled = true, take = 100, skip = 0 } = params || {}

  const isEnabled = enabled && Boolean(activeOrganizationId)

  return useQuery<{ vessels: VesselConnectedAccountAccess[] }>({
    queryKey: ['organization-vessels', activeOrganizationId, { take, skip }],
    queryFn: () => {
      validateOrgId(activeOrganizationId)
      return vesselService.listOrganizationVessels(activeOrganizationId, { take, skip })
    },
    enabled: isEnabled,
  })
}

type VesselAndConnectedAccountsParams = {
  vesselId: string | null
  enabled?: boolean
}

/**
 * TanStack Query hook for fetching a vessel and its connected accounts.
 * Uses activeOrganizationId from context; vesselId is passed as param.
 */
export function useVesselAndConnectedAccountsQuery(params: VesselAndConnectedAccountsParams) {
  const { activeOrganizationId } = useUserContext()
  const { vesselId, enabled = true } = params

  const isEnabled = enabled && Boolean(activeOrganizationId) && Boolean(vesselId)

  return useQuery<VesselAndConnectedAccounts | null>({
    queryKey: ['vessel-connected-accounts', activeOrganizationId, vesselId],
    queryFn: async () => {
      const { orgId, vesselId: validatedVesselId } = validateVesselQueryParams(activeOrganizationId, vesselId)
      const { vesselAndConnectedAccounts } = await vesselService.getVesselAndConnectedAccounts(orgId, validatedVesselId)
      return vesselAndConnectedAccounts
    },
    enabled: isEnabled,
  })
}

type ModuleAvailabilityParams = {
  vesselId: string | null
  enabled?: boolean
}

/**
 * TanStack Query hook for fetching module availability for a vessel.
 * Only runs for privileged users (Admin/Owner). Non-privileged users must not call
 * GET .../vessel/:vesselId/connected-accounts/availability.
 * Uses activeOrganizationId and activeOrganizationRole from context; vesselId is passed as param.
 */
export function useModuleAvailabilityQuery(params: ModuleAvailabilityParams) {
  const { activeOrganizationId, activeOrganizationRole } = useUserContext()
  const { vesselId, enabled = true } = params

  const isPrivileged = isPrivilegedRole(activeOrganizationRole)
  const isEnabled = enabled && isPrivileged && Boolean(activeOrganizationId) && Boolean(vesselId)

  return useQuery<ModuleAvailabilityPayload>({
    queryKey: ['module-availability', activeOrganizationId, vesselId],
    queryFn: () => {
      const { orgId, vesselId: validatedVesselId } = validateVesselQueryParams(activeOrganizationId, vesselId)
      return vesselService.getModuleAvailability(orgId, validatedVesselId)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query mutation hook for creating a vessel.
 * Uses activeOrganizationId from context.
 * Invalidates organization-vessels queries on success.
 *
 * @example
 * ```ts
 * const createVessel = useCreateVesselMutation()
 * const vessel = await createVessel.mutateAsync({ name: 'My Vessel', type: AwxConnectedAccountType.OPERATIONS })
 * ```
 */
export function useCreateVesselMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId } = useUserContext()

  return useMutation({
    mutationFn: async (payload: CreateVesselMutationPayload) => {
      validateOrgId(activeOrganizationId)
      return vesselService.createVessel(activeOrganizationId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-vessels', activeOrganizationId],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for activating a module for a vessel.
 * Backend automatically determines which module to activate and gets accountId from auth context.
 * Uses activeOrganizationId and activeVesselId from context.
 * @returns connectedAccountId string
 */
export function useActivateModuleMutation() {
  const { activeOrganizationId, activeVesselId } = useUserContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { orgId, vesselId } = validateVesselQueryParams(activeOrganizationId, activeVesselId)
      return vesselService.activateModule(orgId, vesselId)
    },
    onSuccess: () => {
      // Invalidate module availability to refresh the UI
      queryClient.invalidateQueries({
        queryKey: ['module-availability', activeOrganizationId, activeVesselId],
      })
    },
  })
}
